import React, { useState, useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { CaretLeft, CaretRight, Info, WarningCircle } from 'phosphor-react';

import { useAuditoriaInteligente } from '../../../../../hooks/useAuditoriaInteligente';
import {
  COLSPAN_RAZAO_MEIO,
  COLSPAN_RAZAO_ROTULO,
  COLUNAS_RAZAO,
  LARGURA_MINIMA_RAZAO,
  N_COLUNAS_RAZAO,
} from './colunas';
import {
  IRazaoConta,
  IRazaoItem,
  fmtCnpj,
  fmtData,
  fmtMoney,
  fmtSaldoComNatureza,
  ouTraco,
} from './papelTrabalho';

/** Mesma paleta da composição — as duas abas são o mesmo documento. */
const COR = {
  ink: '#0F172A',
  muted: '#64748B',
  faint: '#94A3B8',
  brand: '#1B88F5',
  pos: '#16A34A',
  neg: '#DC2626',
  warn: '#D97706',
  warnBg: 'rgba(217, 119, 6, 0.08)',
  linha: 'rgba(15, 23, 42, 0.08)',
} as const;

const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

/** Linhas por página. Conta pesada medida tem 28.988 lançamentos no semestre. */
export const POR_PAGINA = 50;

interface Props {
  empresaId: number;
  conta: string;
  dataIni: string;
  dataFim: string;
  habilitado: boolean;
  /**
   * Lançamento a destacar, vindo do painel de pagamentos do título.
   *
   * `focoOffset` é calculado no backend com a MESMA ordenação do razão:
   * sem ele a tela abriria na primeira página e, em 4.435 linhas, achar o
   * lançamento à mão não é navegação.
   */
  focoNumero?: number | null;
  focoOffset?: number | null;
}

/**
 * Aba RAZÃO: todo o movimento da conta no período, lançamento a lançamento.
 *
 * NÃO é a composição. A composição responde "o que sustenta o saldo hoje" e só
 * mostra o que ficou em aberto; a razão responde "o que aconteceu nesta conta",
 * inclusive o que já se anulou. Medido na 856/177 em jan–jun/2026: 3.325
 * lançamentos de movimento contra dezenas de itens em aberto.
 *
 * Essa diferença de tamanho entre as duas abas é ESPERADA e precisa estar dita
 * na tela — senão o contador abre a Razão, vê um número muito maior e conclui
 * que uma das duas está errada.
 */
export function AbaRazao({
  empresaId,
  conta,
  dataIni,
  dataFim,
  habilitado,
  focoNumero,
  focoOffset,
}: Props) {
  const { getRazaoConta } = useAuditoriaInteligente();
  const [offset, setOffset] = useState(0);

  // Pula para a página do lançamento pedido. Depende de `focoOffset` (e não só
  // do número) porque a página vem do backend: recalcular aqui exigiria repetir
  // a ordenação do razão no cliente — duas réguas para a mesma pergunta.
  useEffect(() => {
    if (focoOffset != null) setOffset(focoOffset);
  }, [focoOffset, focoNumero]);

  const { data, isFetching, isError, error } = useQuery<IRazaoConta>({
    queryKey: [
      'razao-conta',
      { id: empresaId, conta, data_ini: dataIni, data_fim: dataFim, limit: POR_PAGINA, offset },
    ],
    queryFn: getRazaoConta,
    enabled: habilitado,
    placeholderData: anterior => anterior,
  });

  const itens = data?.itens ?? [];
  const pag = data?.paginacao;
  const carregando = isFetching && !data;

  if (isError) {
    return (
      <Flex direction="column" align="center" gap={2} py={14}>
        <WarningCircle size={30} weight="duotone" color="#EF4444" />
        <Text fontSize="sm" color={COR.muted} textAlign="center" maxW="460px">
          Não foi possível carregar a razão desta conta.
          {error instanceof Error && error.message ? ` (${error.message})` : ''}
        </Text>
      </Flex>
    );
  }

  return (
    <>
      {/* ── Cabeçalho: os totais da conta INTEIRA no período ── */}
      <Flex mt={4} align="center" gap={2} wrap="wrap" fontSize="13px">
        <Text fontWeight="700" color={COR.ink}>
          Período:
        </Text>
        <Text color={COR.ink} sx={NUM}>
          {fmtData(dataIni)} a {fmtData(dataFim)}
        </Text>
        <Text color={COR.faint}>·</Text>
        <Text color={COR.muted}>saldo anterior:</Text>
        <Text fontWeight="700" color={COR.ink} sx={NUM}>
          {data?.saldo_anterior === null || data?.saldo_anterior === undefined
            ? '—'
            : fmtSaldoComNatureza(data.saldo_anterior, data.saldo_anterior_natureza)}
        </Text>
        <Text color={COR.faint}>·</Text>
        <Text color={COR.muted}>saldo final:</Text>
        <Text fontWeight="700" color={COR.ink} sx={NUM}>
          {data?.saldo_final === null || data?.saldo_final === undefined
            ? '—'
            : fmtSaldoComNatureza(data.saldo_final, data.saldo_final_natureza)}
        </Text>
      </Flex>

      {/* A diferença de tamanho entre as abas precisa estar DITA. Sem isto o
          contador vê um total muito maior na Razão e acha que divergiu. */}
      <Flex
        mt={3}
        align="flex-start"
        gap={2}
        bg="rgba(27, 136, 245, 0.06)"
        border="1px solid rgba(27, 136, 245, 0.20)"
        borderRadius="lg"
        px={3}
        py={2.5}
      >
        <Box pt="2px">
          <Info size={14} weight="fill" color={COR.brand} />
        </Box>
        <Text fontSize="12.5px" color={COR.muted}>
          A razão mostra <b>todo o movimento do período</b>, inclusive o que já foi baixado. A
          composição ao lado mostra só o que ficou <b>em aberto</b> — por isso ela tem muito menos
          linhas. As duas estão certas: respondem perguntas diferentes.
        </Text>
      </Flex>

      {/* Aviso de acumulado indisponível: honesto, não silencioso. */}
      {data && !data.abertura_disponivel && (
        <Flex
          mt={3}
          align="flex-start"
          gap={2}
          bg={COR.warnBg}
          border="1px solid rgba(217, 119, 6, 0.25)"
          borderRadius="lg"
          px={3}
          py={2.5}
        >
          <Box pt="2px">
            <WarningCircle size={14} weight="fill" color={COR.warn} />
          </Box>
          <Text fontSize="12.5px" color={COR.muted}>
            <b>Saldo de abertura não disponível na base</b> para esta conta, então a coluna
            &quot;Saldo acumulado&quot; aparece como &quot;—&quot;. Os valores e os totais do
            período continuam corretos.
          </Text>
        </Flex>
      )}

      <Box mt={4} overflowX="auto">
        <Table
          size="sm"
          variant="unstyled"
          sx={{ tableLayout: 'fixed' }}
          minW={LARGURA_MINIMA_RAZAO}
        >
          <Thead>
            <Tr borderBottom="1px solid" borderColor={COR.linha}>
              {COLUNAS_RAZAO.map(c => (
                <Th
                  key={c.titulo}
                  fontSize="10px"
                  letterSpacing="0.08em"
                  color={COR.muted}
                  fontWeight="600"
                  py={2.5}
                  px={3}
                  width={c.largura}
                  textAlign={c.alinha ?? 'left'}
                  whiteSpace="nowrap"
                >
                  {c.titulo}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {carregando && (
              <Tr>
                <Td colSpan={N_COLUNAS_RAZAO} py={10}>
                  <Flex justify="center" align="center" gap={2}>
                    <Spinner size="sm" color={COR.brand} />
                    <Text fontSize="sm" color={COR.faint}>
                      Carregando a razão...
                    </Text>
                  </Flex>
                </Td>
              </Tr>
            )}

            {!carregando && itens.length === 0 && (
              <Tr>
                <Td colSpan={N_COLUNAS_RAZAO} py={10}>
                  <Text fontSize="sm" color={COR.faint} textAlign="center">
                    Nenhum lançamento nesta conta no período.
                  </Text>
                </Td>
              </Tr>
            )}

            {!carregando &&
              itens.map(item => (
                <LinhaRazao
                  key={item.id}
                  item={item}
                  destaque={focoNumero != null && Number(item.numero) === focoNumero}
                />
              ))}

            {/* TOTAIS DA CONTA INTEIRA — não mudam com a página. */}
            {!carregando && itens.length > 0 && (
              <Tr borderTop="2px solid" borderColor="rgba(15, 23, 42, 0.15)">
                <Td colSpan={COLSPAN_RAZAO_ROTULO} px={3} py={3}>
                  <Text
                    fontSize="12px"
                    fontWeight="700"
                    color={COR.ink}
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                  >
                    Movimento do período
                  </Text>
                </Td>
                {/* Cai sob "Valor", que é a coluna que estes números somam. */}
                <Td px={3} py={3} textAlign="right">
                  <Text fontSize="12px" color={COR.muted} sx={NUM}>
                    D {fmtMoney(data?.total_debito)}
                  </Text>
                  <Text fontSize="12px" color={COR.muted} sx={NUM}>
                    C {fmtMoney(data?.total_credito)}
                  </Text>
                </Td>
                <Td colSpan={COLSPAN_RAZAO_MEIO} />
                <Td px={3} py={3} textAlign="right">
                  <Text fontSize="13px" fontWeight="700" color={COR.ink} sx={NUM}>
                    {data?.saldo_final === null || data?.saldo_final === undefined
                      ? '—'
                      : fmtMoney(data.saldo_final)}
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* ── Paginação ── */}
      {pag?.paginado && pag.paginas > 1 && (
        <Flex
          mt={3}
          align="center"
          gap={2}
          wrap="wrap"
          justify="space-between"
          borderTop="1px solid"
          borderColor={COR.linha}
          pt={3}
        >
          <Text fontSize="12.5px" color={COR.muted}>
            Mostrando{' '}
            <b>
              {pag.offset + 1}–{Math.min(pag.offset + POR_PAGINA, pag.total_itens)}
            </b>{' '}
            de <b>{pag.total_itens}</b> lançamentos
          </Text>
          <Flex align="center" gap={2}>
            <Button
              size="xs"
              variant="ghost"
              leftIcon={<CaretLeft size={12} weight="bold" />}
              isDisabled={offset === 0 || isFetching}
              onClick={() => setOffset(Math.max(0, offset - POR_PAGINA))}
            >
              Anterior
            </Button>
            <Text fontSize="12.5px" color={COR.muted} sx={NUM}>
              Página {Math.floor(offset / POR_PAGINA) + 1} de {pag.paginas}
            </Text>
            <Button
              size="xs"
              variant="ghost"
              rightIcon={<CaretRight size={12} weight="bold" />}
              isDisabled={offset + POR_PAGINA >= pag.total_itens || isFetching}
              onClick={() => setOffset(offset + POR_PAGINA)}
            >
              Próxima
            </Button>
          </Flex>
        </Flex>
      )}
    </>
  );
}

/**
 * Uma linha da razão.
 *
 * O "Tipo" mostra D/C — o que `extract_lancamentos` afirma. A planilha usa
 * PROVISAO/PAGAMENTO, mas chamar de provisão o que é só um crédito seria
 * interpretar o dado: a mesma conta credora recebe créditos que não são
 * provisão (estorno, transferência), e o rótulo errado levaria o contador a
 * conclusões erradas.
 */
/**
 * O fundo da linha. Função nomeada em vez de ternário aninhado: o destaque da
 * navegação vence a marca de abertura, e a precedência precisa estar explícita.
 */
const fundoDaLinha = (destaque?: boolean, abertura?: boolean): string | undefined => {
  if (destaque) return 'rgba(27, 136, 245, 0.10)';
  if (abertura) return 'rgba(15, 23, 42, 0.03)';
  return undefined;
};

function LinhaRazao({ item, destaque }: { item: IRazaoItem; destaque?: boolean }) {
  const celula = { px: 3, py: 2.5, fontSize: '13px', color: COR.ink } as const;
  const credito = item.lado === 'C';
  const refLinha = React.useRef<HTMLTableRowElement>(null);

  // Rolar até a linha destacada. Sem isto a página certa abriria, mas o
  // lançamento poderia ficar fora da área visível — e o usuário pediu para
  // "ir direto para a linha dele".
  useEffect(() => {
    if (destaque && refLinha.current) {
      refLinha.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [destaque]);

  return (
    <Tr
      ref={refLinha}
      borderBottom="1px solid"
      borderColor={destaque ? '#1B88F5' : COR.linha}
      bg={fundoDaLinha(destaque, item.abertura)}
      _hover={{ bg: destaque ? 'rgba(27, 136, 245, 0.14)' : 'rgba(15, 23, 42, 0.03)' }}
    >
      <Td {...celula} sx={NUM} whiteSpace="nowrap">
        {fmtData(item.data)}
      </Td>
      <Td {...celula} sx={NUM}>
        {ouTraco(item.numero)}
      </Td>
      <Td {...celula}>
        <Badge
          borderRadius="full"
          px={2}
          fontSize="10px"
          fontWeight="700"
          textTransform="none"
          bg={credito ? 'rgba(22, 163, 74, 0.10)' : 'rgba(220, 38, 38, 0.08)'}
          color={credito ? COR.pos : COR.neg}
          title={credito ? 'Crédito nesta conta' : 'Débito nesta conta'}
        >
          {item.lado}
        </Badge>
      </Td>
      <Td {...celula} textAlign="right" sx={NUM}>
        {fmtMoney(item.valor)}
      </Td>
      <Td {...celula} sx={NUM}>
        {ouTraco(item.nota_numero)}
      </Td>
      <Td {...celula}>
        <Text noOfLines={1} title={item.fornecedor ?? undefined}>
          {ouTraco(item.fornecedor)}
        </Text>
      </Td>
      <Td {...celula} sx={NUM} whiteSpace="nowrap">
        {fmtCnpj(item.fornecedor_cnpj)}
      </Td>
      <Td {...celula} sx={NUM}>
        {ouTraco(item.filial)}
      </Td>
      <Td {...celula}>
        <Text noOfLines={2} title={item.contrapartida_label ?? undefined}>
          {ouTraco(item.contrapartida_label)}
        </Text>
      </Td>
      <Td {...celula}>
        <Text noOfLines={1} title={item.historico ?? undefined}>
          {ouTraco(item.historico)}
        </Text>
      </Td>
      <Td {...celula} textAlign="right" sx={NUM} fontWeight="600">
        {/* `null` quando a base não alcança a abertura: "—" em vez de um
            acumulado partindo de um zero inventado. */}
        {item.saldo_acumulado === null || item.saldo_acumulado === undefined
          ? '—'
          : fmtMoney(item.saldo_acumulado)}
      </Td>
    </Tr>
  );
}
